/**
 * FR-28 — White-label / App Settings
 * Accessible via Settings menu (SUPER_ADMIN only sees the full editor;
 * other admins see read-only branding info).
 *
 * Keys: institution_name · logo_url · primary_color · footer_text · tagline ·
 * description · cover_image_url · favicon_url · login_headline · login_layout ·
 * hide_platform_branding · maritime_fields
 *
 * Super Admin: editor dengan unggah gambar dan pratinjau halaman masuk
 * (LoginPageEditor). Admin lain: daftar baca-saja.
 */
import React from 'react'
import toast from 'react-hot-toast'
import { useApp } from '../context/AppContext'
import { Icon, useT } from '../shared'
import { Btn } from '../shared/components'
import BrandLogo, { useOrgName } from '../shared/BrandLogo'
import { getSettings, updateSettings, getMyPlan } from '../api/settings.api'
import { requestEmailChange, get2faStatus } from '../api/auth.api'
import { exportUserData } from '../api/users.api'
import { HEX } from '../lib/color'
import {
  Row, ImageField, ColorField, LayoutPicker, LoginPreview, IMAGE_LIMITS,
} from './LoginPageEditor'

/**
 * Ganti email akun sendiri. Dua tahap, dan sengaja lebih ketat daripada mengubah
 * nama: email adalah tujuan pemulihan kata sandi. Di sini hanya tahap pertama —
 * kata sandi (dan kode 2FA bila aktif), lalu tautan dikirim ke alamat BARU.
 * Emailnya baru berubah setelah tautan itu dibuka.
 */
function ChangeEmailCard({ lang, email }) {
  const [open, setOpen] = React.useState(false)
  const [newEmail, setNewEmail] = React.useState('')
  const [password, setPassword] = React.useState('')
  const [code, setCode] = React.useState('')
  const [needCode, setNeedCode] = React.useState(false)
  const [busy, setBusy] = React.useState(false)
  const [sentTo, setSentTo] = React.useState(null)

  React.useEffect(() => {
    if (!open) return
    get2faStatus().then((s) => setNeedCode(Boolean(s.enabled))).catch(() => {})
  }, [open])

  const reset = () => { setOpen(false); setNewEmail(''); setPassword(''); setCode('') }

  const submit = async (e) => {
    e.preventDefault()
    setBusy(true)
    try {
      const r = await requestEmailChange({ newEmail: newEmail.trim(), password, code: code.trim() })
      setSentTo(r.sentTo)
      reset()
    } catch (err) {
      if (err?.response?.data?.code === 'MFA_CODE_REQUIRED') setNeedCode(true)
      toast.error(err?.response?.data?.error || err.message)
      setCode('')
    } finally { setBusy(false) }
  }

  return (
    <div className="card mb-4" style={{ maxWidth: 640, padding: '20px 24px' }}>
      <div className="row between" style={{ alignItems: 'center', gap: 16 }}>
        <div>
          <div style={{ fontWeight: 600 }}>{lang === 'id' ? 'Email akun' : 'Account email'}</div>
          <div className="muted" style={{ fontSize: 13, marginTop: 2 }}>{email || '—'}</div>
        </div>
        {!open && <Btn onClick={() => { setSentTo(null); setOpen(true) }}>{lang === 'id' ? 'Ganti email' : 'Change email'}</Btn>}
      </div>
      {sentTo && (
        <div className="muted" style={{ fontSize: 13, marginTop: 12 }}>
          {lang === 'id'
            ? `Tautan konfirmasi dikirim ke ${sentTo}. Email baru berlaku setelah tautan itu dibuka (berlaku 1 jam), dan semua sesi akan berakhir. Alamat lama juga diberi tahu.`
            : `A confirmation link was sent to ${sentTo}. The new email applies once you open it (valid 1 hour), and all sessions will end. The old address is notified too.`}
        </div>
      )}
      {open && (
        <form onSubmit={submit} className="col gap-3" style={{ marginTop: 16 }}>
          <div className="field">
            <label className="field-label">{lang === 'id' ? 'Email baru' : 'New email'}</label>
            <input className="input" type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} required autoFocus />
          </div>
          <div className="field">
            <label className="field-label">{lang === 'id' ? 'Kata sandi saat ini' : 'Current password'}</label>
            <input className="input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" required />
          </div>
          {needCode && (
            <div className="field">
              <label className="field-label">{lang === 'id' ? 'Kode 2FA' : '2FA code'}</label>
              <input className="input" value={code} onChange={(e) => setCode(e.target.value)} inputMode="numeric" autoComplete="one-time-code" maxLength={7}
                style={{ letterSpacing: 4, fontFamily: 'var(--font-mono)' }} />
            </div>
          )}
          <div className="row gap-2">
            <Btn variant="primary" type="submit" disabled={busy || !newEmail || !password || (needCode && code.trim().length < 6)}>
              {busy ? (lang === 'id' ? 'Mengirim…' : 'Sending…') : (lang === 'id' ? 'Kirim tautan konfirmasi' : 'Send confirmation link')}
            </Btn>
            <Btn type="button" onClick={reset}>{lang === 'id' ? 'Batal' : 'Cancel'}</Btn>
          </div>
        </form>
      )}
    </div>
  )
}

/**
 * Hak pemilik data (UU PDP): admin mengunduh salinan data pribadinya sendiri di
 * sini. Peserta dan pelatih tidak punya halaman Pengaturan; mereka memintanya ke
 * admin, yang mengunduhkannya dari layar Pengguna.
 */
function MyDataCard({ lang }) {
  const [busy, setBusy] = React.useState(false)

  const download = async () => {
    setBusy(true)
    try {
      const { blob, filename } = await exportUserData('me')
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      a.click()
      URL.revokeObjectURL(url)
    } catch (e) {
      toast.error(e?.response?.data?.error || e.message)
    } finally { setBusy(false) }
  }

  return (
    <div className="card mb-4" style={{ maxWidth: 640, padding: '20px 24px' }}>
      <div className="row between" style={{ alignItems: 'center', gap: 16 }}>
        <div>
          <div style={{ fontWeight: 600 }}>{lang === 'id' ? 'Data saya' : 'My data'}</div>
          <div className="muted" style={{ fontSize: 13, marginTop: 2 }}>
            {lang === 'id' ? 'Unduh salinan data pribadi akun ini.' : 'Download a copy of this account’s personal data.'}
          </div>
        </div>
        <Btn onClick={download} disabled={busy}>
          {busy ? (lang === 'id' ? 'Menyiapkan…' : 'Preparing…') : (lang === 'id' ? 'Unduh data saya' : 'Download my data')}
        </Btn>
      </div>
    </div>
  )
}

export default function SettingsScreen() {
  const { lang, user, setBranding, navigate } = useApp()
  const orgName = useOrgName()
  const t = useT()
  const isSuperAdmin = user?.role === 'SUPER_ADMIN'

  const [form, setForm]     = React.useState(null)   // null = loading
  const [saving, setSaving] = React.useState(false)
  const [dirty, setDirty]   = React.useState(false)

  const [plan, setPlan] = React.useState(null)

  React.useEffect(() => {
    getSettings()
      .then((s) => setForm(s))
      .catch(() => setForm({}))
    // Non-fatal: the panel simply doesn't render if this fails.
    getMyPlan().then(setPlan).catch(() => {})
  }, [])

  function handleChange(key, val) {
    setForm((f) => ({ ...f, [key]: val }))
    setDirty(true)
  }

  // Unggah dan hapus gambar langsung tersimpan di server dan mengembalikan
  // branding terbaru. Hanya kolom gambarnya yang dipakai untuk menimpa isian
  // formulir: mengganti seluruh `form` akan membuang ketikan yang belum
  // disimpan di kolom lain. Branding global ikut diperbarui supaya sidebar dan
  // halaman masuk berubah tanpa perlu memuat ulang.
  const IMAGE_KEYS = ['logo_url', 'cover_image_url', 'favicon_url']
  function handleImageSaved(b) {
    setForm((f) => ({ ...f, ...Object.fromEntries(IMAGE_KEYS.map((k) => [k, b[k] || ''])) }))
    setBranding(b)
  }

  // Elemen di atas paket lembaga ditampilkan terkunci. Server tetap yang
  // memutuskan (403 FEATURE_NOT_IN_PLAN); ini hanya mencegah admin mengisi
  // sesuatu yang pasti ditolak. Bila paketnya belum termuat, tidak dikunci.
  // Warna yang diketik setengah jalan atau salah format tidak boleh dikirim: server
  // menolaknya, dan admin tinggal membaca galat setelah menekan Simpan. Pesan
  // di bawah kolom warna sudah menjelaskan sebabnya.
  const colorInvalid = !!form?.primary_color && !HEX.test(form.primary_color)

  const advanced = plan?.features ? plan.features.login_advanced === true : true
  const whiteLabel = plan?.features ? plan.features.white_label === true : true
  const LOCK_ADVANCED = lang === 'id' ? 'Paket 2 ke atas' : 'Plan 2 and above'
  const LOCK_WHITE = 'Enterprise'

  async function handleSave() {
    setSaving(true)
    try {
      // Built from the field list rather than spelled out, so adding a field
      // above is enough to make it savable. Listing them by hand here is how
      // three fields ended up editable on screen but silently discarded.
      //
      // Checkbox field TIDAK ikut `|| ''` — untuk boolean, `false || ''`
      // menjadi string kosong, dan backend menolaknya (maritime_fields wajib
      // boolean di Zod). Mematikan toggle lalu Simpan akan gagal 400 kalau
      // ini ikut aturan default field teks.
      const payload = {
        ...Object.fromEntries(
          fields.map((f) => [f.key, f.type === 'checkbox' ? !!form[f.key] : form[f.key] || ''])
        ),
        favicon_url: form.favicon_url || '',
        login_headline: form.login_headline || '',
        login_layout: form.login_layout || 'SPLIT',
        hide_platform_branding: !!form.hide_platform_branding,
      }
      const updated = await updateSettings(payload)
      setForm(updated)
      setBranding(updated)
      setDirty(false)
      toast.success(lang === 'id' ? 'Pengaturan disimpan' : 'Settings saved')
    } catch (err) {
      toast.error(err?.response?.data?.error || err.message)
    } finally {
      setSaving(false)
    }
  }

  const fields = [
    {
      key: 'institution_name',
      label: lang === 'id' ? 'Nama Institusi' : 'Institution Name',
      hint:  lang === 'id' ? 'Ditampilkan di sidebar dan email.' : 'Shown in the sidebar and emails.',
      type:  'text',
    },
    {
      key: 'logo_url',
      label: lang === 'id' ? 'URL Logo' : 'Logo URL',
      hint:  lang === 'id' ? 'URL gambar PNG/SVG. Kosongkan untuk memakai lambang Gladhy.' : 'PNG/SVG image URL. Leave blank to use the Gladhy mark.',
      type:  'url',
    },
    {
      // Namanya "override", tetapi bukan itu yang dilakukannya — warna program
      // selalu menang (lihat context/AppContext.jsx). Nilai ini dipakai saat
      // belum ada program aktif: halaman masuk, dan sesaat sebelum peserta
      // memilih programnya. Keterangan lama berbunyi "kosongkan = ikuti warna
      // program", yang membuat admin mengira mengisinya akan menimpa warna
      // program — lalu bingung karena tampilan tidak berubah sama sekali.
      key: 'primary_color',
      label: lang === 'id' ? 'Warna Halaman Masuk' : 'Sign-in Page Colour',
      hint:  lang === 'id'
        ? 'Hex (#1E40AF), dipakai di halaman masuk. Di dalam aplikasi, warna mengikuti program yang sedang dibuka.'
        : 'Hex (#1E40AF), used on the sign-in page. Inside the app, colour follows the program being viewed.',
      type:  'text',
    },
    {
      key: 'footer_text',
      label: lang === 'id' ? 'Sub-judul Lembaga' : 'Institution Subtitle',
      hint:  lang === 'id' ? 'Baris kecil di bawah nama: di sidebar, halaman masuk, dan kaki email.' : 'The small line under your name: sidebar, sign-in page, and email footer.',
      type:  'text',
    },
    // These three shape the sign-in page an institution's own participants
    // see. They existed in the API but had no field here, so nobody could set
    // them — which is why that page kept showing whatever was hardcoded.
    {
      key: 'tagline',
      label: lang === 'id' ? 'Tagline Halaman Masuk' : 'Sign-in Tagline',
      hint:  lang === 'id' ? 'Label pendek di atas judul, mis. "Pelatihan Bersertifikat".' : 'Short label above the headline, e.g. "Certified Training".',
      type:  'text',
    },
    {
      key: 'description',
      label: lang === 'id' ? 'Deskripsi Halaman Masuk' : 'Sign-in Description',
      hint:  lang === 'id' ? 'Satu paragraf tentang lembaga Anda. Kosongkan untuk teks bawaan.' : 'One paragraph about your institution. Leave blank for the default text.',
      type:  'text',
    },
    {
      key: 'cover_image_url',
      label: lang === 'id' ? 'Gambar Halaman Masuk' : 'Sign-in Background Image',
      hint:  lang === 'id' ? 'URL foto lanskap (min. 1600px). Kosongkan untuk gradien polos mengikuti warna utama.' : 'Landscape photo URL (1600px+). Leave blank for a plain gradient built from your primary colour.',
      type:  'url',
    },
    // Ada di API sejak lama (lihat backend routes/settings.js), tapi tidak
    // pernah punya kolom di layar ini — satu-satunya cara menyalakannya
    // adalah panggil API mentah. Field ini yang menutup celah itu: dinyalakan
    // sini artinya kolom No./Exp. Sertifikat Nautika & Hubla muncul di wizard
    // impor peserta DAN di tombol edit per-peserta di tab Participants.
    {
      key: 'maritime_fields',
      label: lang === 'id' ? 'Kredensial Maritim' : 'Maritime Credentials',
      hint:  lang === 'id'
        ? 'Nyalakan untuk lembaga pelatihan maritim — menampilkan kolom No./kedaluwarsa Sertifikat Nautika & Hubla saat impor peserta dan di halaman Participants.'
        : 'Turn on for maritime training institutions — shows Nautical & Hubla certificate number/expiry fields during participant import and on the Participants page.',
      type:  'checkbox',
    },
  ]

  const fieldTile = (f) => (
            <div key={f.key} className="tile" style={{ padding: '20px 24px' }}>
              {/* htmlFor/id dipasangkan supaya mengeklik label memindahkan
                  fokus, dan pembaca layar tidak menerima label yatim. */}
              <label
                htmlFor={`setelan-${f.key}`}
                className="small"
                style={{ fontWeight: 600, display: 'block', marginBottom: 6 }}
              >
                {f.label}
              </label>
              {isSuperAdmin ? (
                f.type === 'checkbox' ? (
                  <>
                    {/* Checkbox pakai `checked`, bukan `value` — input teks di
                        bawah tidak cocok untuk boolean (lihat catatan
                        handleSave soal `|| ''` yang merusak `false`). */}
                    <label className="row gap-2" style={{ alignItems: 'center', cursor: 'pointer' }}>
                      <input
                        id={`setelan-${f.key}`}
                        name={f.key}
                        type="checkbox"
                        checked={!!form[f.key]}
                        onChange={(e) => handleChange(f.key, e.target.checked)}
                      />
                      <span className="small">
                        {form[f.key]
                          ? (lang === 'id' ? 'Aktif' : 'On')
                          : (lang === 'id' ? 'Mati' : 'Off')}
                      </span>
                    </label>
                    <div className="tiny muted mt-2">{f.hint}</div>
                  </>
                ) : (
                  <>
                    {/* .input, bukan .search-input — yang terakhir itu kotak
                        pencarian selebar 220px dengan ruang kiri untuk ikon kaca
                        pembesar, sehingga kolom di layar ini tampil beda tinggi
                        dan beda tepi dari form lain di aplikasi. */}
                    <input
                      id={`setelan-${f.key}`}
                      name={f.key}
                      type={f.type}
                      value={form[f.key] || ''}
                      onChange={(e) => handleChange(f.key, e.target.value)}
                      className="input"
                      style={{ width: '100%' }}
                    />
                    {/* Keterangan hanya di satu tempat. Sebelumnya kalimat yang
                        sama muncul dua kali: sebagai placeholder di dalam kotak
                        DAN sebagai keterangan di bawahnya. */}
                    <div className="tiny muted mt-2">{f.hint}</div>
                  </>
                )
              ) : (
                <div className="mono small" style={{ color: 'var(--text-2)', padding: '6px 0' }}>
                  {f.type === 'checkbox'
                    ? (form[f.key] ? (lang === 'id' ? 'Aktif' : 'On') : (lang === 'id' ? 'Mati' : 'Off'))
                    : (form[f.key] || <span className="muted">—</span>)}
                </div>
              )}
            </div>
  )

  const sidebarPreview = form?.institution_name && (
    <div className="tile" style={{ padding: '20px 24px' }}>
      <div className="small" style={{ fontWeight: 600, marginBottom: 12 }}>
        {lang === 'id' ? 'Preview Sidebar' : 'Sidebar Preview'}
      </div>
      <div className="row gap-3" style={{
        background: 'var(--surface)',
        borderRadius: 'var(--r-md)',
        padding: '12px 16px',
        border: '1px solid var(--border)',
        alignItems: 'center',
      }}>
        <BrandLogo size={28} branding={form} />
        <div>
          <div className="small" style={{ fontWeight: 700 }}>{form.institution_name}</div>
          <div className="tiny muted">
            {form.footer_text || (form.hide_platform_branding ? '' : (lang === 'id' ? 'Platform Pelatihan & Sertifikasi' : 'Training & Certification Platform'))}
          </div>
        </div>
      </div>
    </div>
  )

  return (
    <div className="main-inner">
      <div className="page-header">
        <div className="page-header-text">
          <div className="eyebrow">{orgName} · {lang === 'id' ? 'Pengaturan' : 'Settings'}</div>
          <h1 className="h1" style={{ marginTop: 6 }}>
            {lang === 'id' ? 'Pengaturan & Branding' : 'Settings & Branding'}
          </h1>
          <div className="muted mt-1">
            {lang === 'id'
              ? 'Kustomisasi nama institusi, logo, dan warna untuk white-label.'
              : 'Customise institution name, logo, and colour for white-labelling.'}
          </div>
        </div>
        {isSuperAdmin && dirty && (
          <div className="page-header-actions">
            <Btn variant="primary" onClick={handleSave} disabled={saving || colorInvalid}
              title={colorInvalid ? (lang === 'id' ? 'Perbaiki format warna dulu (#RRGGBB)' : 'Fix the colour format first (#RRGGBB)') : undefined}
              icon={<Icon name="check" size={14} />}>
              {saving ? (lang === 'id' ? 'Menyimpan…' : 'Saving…') : (lang === 'id' ? 'Simpan' : 'Save')}
            </Btn>
          </div>
        )}
      </div>

      {/* Keamanan akun — 2FA. Tersedia untuk semua staf; wajib bagi peran admin. */}
      {user?.role !== 'PARTICIPANT' && (
        <div className="card mb-4" style={{ maxWidth: 640, padding: '20px 24px' }}>
          <div className="row between" style={{ alignItems: 'center', gap: 16 }}>
            <div>
              <div style={{ fontWeight: 600 }}>{lang === 'id' ? 'Verifikasi dua langkah (2FA)' : 'Two-step verification (2FA)'}</div>
              <div className="muted" style={{ fontSize: 13, marginTop: 2 }}>
                {lang === 'id'
                  ? 'Lindungi akun Anda dengan kode dari aplikasi autentikator saat masuk.'
                  : 'Protect your account with a code from an authenticator app at sign-in.'}
              </div>
            </div>
            <Btn onClick={() => navigate('mfa-setup')}>{lang === 'id' ? 'Kelola' : 'Manage'}</Btn>
          </div>
        </div>
      )}

      {/* Email akun — dua tahap, lihat ChangeEmailCard. */}
      {user?.role !== 'PARTICIPANT' && <ChangeEmailCard lang={lang} email={user?.email} />}
      <MyDataCard lang={lang} />

      {/* Subscription panel. Shown before the branding editor because a
          frozen institution needs to understand why things are failing before
          they wonder about their logo. */}
      {plan && !plan.platformStaff && (
        <div
          className="card mb-4"
          style={{
            maxWidth: 640,
            // .card hanya mengatur latar, tepi, dan sudut — paddingnya ada di
            // .card-body. Kartu ini tidak memakainya, jadi tanpa padding di
            // sini teksnya menempel ke garis tepi.
            padding: '20px 24px',
            borderColor: plan.isExpired ? 'var(--danger)' : undefined,
          }}
        >
          <div className="row between mb-2" style={{ flexWrap: 'wrap', gap: 8 }}>
            <strong>{lang === 'id' ? 'Paket Langganan' : 'Subscription'}</strong>
            <span className="small mono">{plan.label}</span>
          </div>

          {plan.isExpired ? (
            <p className="small" style={{ color: 'var(--danger)', margin: '0 0 12px' }}>
              {lang === 'id'
                ? 'Masa aktif sudah berakhir. Data lama tetap dapat dilihat dan diunduh, tetapi penambahan data baru dinonaktifkan sampai diperpanjang.'
                : 'Your subscription has ended. Existing data stays viewable and downloadable, but nothing new can be added until it is renewed.'}
            </p>
          ) : (
            <p className="small muted" style={{ margin: '0 0 12px' }}>
              {plan.expiresAt
                ? `${lang === 'id' ? 'Berlaku sampai' : 'Valid until'} ${plan.expiresAt.slice(0, 10)}`
                : (lang === 'id' ? 'Tanpa batas waktu' : 'No expiry')}
            </p>
          )}

          {/* Usage first: this is what an admin actually wants to know. */}
          {[
            { key: 'participants', label: lang === 'id' ? 'Peserta' : 'Participants' },
            { key: 'programs', label: lang === 'id' ? 'Program' : 'Programs' },
          ].map(({ key, label }) => {
            const used = plan.usage?.[key] ?? 0
            // Batas dianggap ada hanya bila angka positif. Nol pernah lolos ke
            // sini dan menghasilkan "3/0" dengan bilah merah penuh — used/0
            // adalah Infinity, yang dijepit menjadi 100%. Yang tampil seolah
            // kuota terlampaui, padahal paketnya tak terbatas.
            const rawLimit = plan.limits?.[key]
            const limit = typeof rawLimit === 'number' && rawLimit > 0 ? rawLimit : null
            const pct = limit ? Math.min(100, Math.round((used / limit) * 100)) : 0
            return (
              <div key={key} style={{ marginBottom: 10 }}>
                <div className="row between" style={{ fontSize: 13 }}>
                  <span>{label}</span>
                  <span className="mono" style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {used}/{limit == null ? '∞' : limit}
                  </span>
                </div>
                {limit != null && (
                  <div className="progress" style={{ marginTop: 4 }}>
                    <div
                      className="fill"
                      style={{
                        width: `${pct}%`,
                        background: pct >= 100 ? 'var(--danger)' : pct >= 80 ? 'var(--amber)' : 'var(--program-color)',
                      }}
                    />
                  </div>
                )}
              </div>
            )
          })}

          <p className="tiny muted" style={{ marginTop: 12, marginBottom: 0 }}>
            {lang === 'id'
              ? 'Untuk menaikkan paket atau memperpanjang masa aktif, hubungi pengelola platform.'
              : 'To upgrade or renew, contact the platform operator.'}
          </p>
        </div>
      )}

      {form === null ? (
        <div className="card" style={{ padding: 40, textAlign: 'center' }}>
          <p className="muted">{lang === 'id' ? 'Memuat…' : 'Loading…'}</p>
        </div>
      ) : isSuperAdmin ? (
        <div className="settings-split">
          <div className="col gap-4" style={{ minWidth: 0 }}>
            {fieldTile(fields.find((f) => f.key === 'institution_name'))}

            <ImageField
              kind="logo" field="logo_url" value={form.logo_url} lang={lang}
              label="Logo"
              hint={lang === 'id'
                ? 'PNG, JPG, atau WEBP · maks. 1 MB · persegi lebih baik (ditampilkan bulat). Tanpa logo, dipakai lambang Gladhy.'
                : 'PNG, JPG or WEBP · max 1 MB · square works best (shown round). Without a logo the Gladhy mark is used.'}
              maxBytes={IMAGE_LIMITS.logo}
              onSaved={handleImageSaved}
            />

            {fieldTile(fields.find((f) => f.key === 'footer_text'))}

            <Row
              label={lang === 'id' ? 'Warna Utama' : 'Primary Colour'}
              htmlFor="setelan-primary_color" lang={lang}
              hint={lang === 'id'
                ? 'Dipakai di halaman masuk dan tombol utamanya. Di dalam aplikasi, warna mengikuti program yang sedang dibuka.'
                : 'Used on the sign-in page and its main button. Inside the app, colour follows the program being viewed.'}
            >
              <ColorField value={form.primary_color} onChange={(v) => handleChange('primary_color', v)} lang={lang} />
            </Row>

            <ImageField
              kind="favicon" field="favicon_url" value={form.favicon_url} lang={lang}
              label={lang === 'id' ? 'Ikon Tab Peramban (Favicon)' : 'Browser Tab Icon (Favicon)'}
              hint={lang === 'id'
                ? 'PNG atau ICO · maks. 1 MB · persegi, mis. 64×64. Kosong: dipakai ikon Gladhy.'
                : 'PNG or ICO · max 1 MB · square, e.g. 64×64. Blank: the Gladhy icon is used.'}
              accept={['png', 'ico']}
              maxBytes={IMAGE_LIMITS.favicon}
              onSaved={handleImageSaved}
            />

            <div className="eyebrow" style={{ marginTop: 8 }}>
              {lang === 'id' ? 'Tampilan Halaman Masuk' : 'Sign-in Page Appearance'}
            </div>

            <Row
              label={lang === 'id' ? 'Tata Letak' : 'Layout'}
              lock={advanced ? null : LOCK_ADVANCED} lang={lang}
            >
              <LayoutPicker
                value={form.login_layout}
                onChange={(v) => handleChange('login_layout', v)}
                disabled={!advanced} lang={lang}
              />
            </Row>

            <Row
              label={lang === 'id' ? 'Judul Besar' : 'Headline'}
              htmlFor="setelan-login_headline"
              lock={advanced ? null : LOCK_ADVANCED} lang={lang}
              hint={lang === 'id'
                ? 'Maks. 80 karakter; kata terakhir disorot. Kosong: "Latih. Uji. Sertifikasi.".'
                : 'Max 80 characters; the last word is highlighted. Blank: "Train. Assess. Certify.".'}
            >
              <input
                id="setelan-login_headline" name="login_headline" className="input" style={{ width: '100%' }}
                value={form.login_headline || ''} maxLength={80} disabled={!advanced}
                onChange={(e) => handleChange('login_headline', e.target.value)}
              />
            </Row>

            <Row
              label="Tagline"
              htmlFor="setelan-tagline"
              lock={advanced ? null : LOCK_ADVANCED} lang={lang}
              hint={lang === 'id' ? 'Label pendek di atas judul, mis. "Pelatihan Bersertifikat".' : 'Short label above the headline, e.g. "Certified Training".'}
            >
              <input
                id="setelan-tagline" name="tagline" className="input" style={{ width: '100%' }}
                value={form.tagline || ''} maxLength={160} disabled={!advanced}
                onChange={(e) => handleChange('tagline', e.target.value)}
              />
            </Row>

            <Row
              label={lang === 'id' ? 'Deskripsi' : 'Description'}
              htmlFor="setelan-description"
              lock={advanced ? null : LOCK_ADVANCED} lang={lang}
              hint={lang === 'id' ? 'Satu paragraf tentang lembaga Anda (maks. 1000 karakter). Kosong: teks bawaan.' : 'One paragraph about your institution (max 1000 characters). Blank: default text.'}
            >
              <textarea
                id="setelan-description" name="description" className="input" rows={4}
                style={{ width: '100%', resize: 'vertical', height: 'auto', paddingTop: 8 }}
                value={form.description || ''} maxLength={1000} disabled={!advanced}
                onChange={(e) => handleChange('description', e.target.value)}
              />
            </Row>

            <ImageField
              kind="cover" field="cover_image_url" value={form.cover_image_url} lang={lang}
              label={lang === 'id' ? 'Gambar Latar' : 'Background Image'}
              hint={lang === 'id'
                ? 'PNG, JPG, atau WEBP · maks. 5 MB · foto lanskap min. 1600 px. Kosong: gradien dari warna utama.'
                : 'PNG, JPG or WEBP · max 5 MB · landscape photo, 1600 px or wider. Blank: gradient from the primary colour.'}
              maxBytes={IMAGE_LIMITS.cover}
              lock={advanced ? null : LOCK_ADVANCED} locked={!advanced}
              onSaved={handleImageSaved}
            />

            <Row
              label={lang === 'id' ? 'Sembunyikan Identitas Gladhy' : 'Hide Gladhy Branding'}
              htmlFor="setelan-hide_platform_branding"
              lock={whiteLabel ? null : LOCK_WHITE} lang={lang}
              hint={lang === 'id'
                ? 'White-label penuh: tanpa teks "Platform Pelatihan & Sertifikasi", tanpa lambang Gladhy (diganti huruf pertama nama lembaga bila belum ada logo), dan tanpa "Powered by Gladhy" di halaman publik.'
                : 'Full white-label: no "Training & Certification Platform" text, no Gladhy mark (replaced by the initial of your name when there is no logo) and no "Powered by Gladhy" on public pages.'}
            >
              <label className="row gap-2" style={{ alignItems: 'center', cursor: whiteLabel ? 'pointer' : 'not-allowed' }}>
                <input
                  id="setelan-hide_platform_branding" name="hide_platform_branding" type="checkbox"
                  checked={!!form.hide_platform_branding} disabled={!whiteLabel}
                  onChange={(e) => handleChange('hide_platform_branding', e.target.checked)}
                />
                <span className="small">
                  {form.hide_platform_branding ? (lang === 'id' ? 'Aktif' : 'On') : (lang === 'id' ? 'Mati' : 'Off')}
                </span>
              </label>
            </Row>

            {fieldTile(fields.find((f) => f.key === 'maritime_fields'))}
            {sidebarPreview}
          </div>

          <div className="settings-preview" style={{ minWidth: 0 }}>
            <LoginPreview draft={form} lang={lang} />
          </div>
        </div>
      ) : (
        <div className="col gap-4" style={{ maxWidth: 640 }}>
          {fields.map(fieldTile)}

          {sidebarPreview}

          {!isSuperAdmin && (
            <div className="card" style={{ padding: '14px 20px', background: 'var(--surface-2)' }}>
              <div className="small muted">
                <Icon name="lock" size={12} style={{ marginRight: 6 }} />
                {lang === 'id'
                  ? 'Hanya Super Admin yang dapat mengubah pengaturan ini.'
                  : 'Only Super Admins can edit these settings.'}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
